import { Alert, Box, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { Save } from '@mui/icons-material';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
import {
  ActionIconButton,
  CustomAutoComplete,
  CustomButton,
  CustomTextField,
  DrawerForm,
  DrawerFormActions,
  DrawerFormActionSummary,
  DrawerFormSection,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  renderTwoLineOption,
  RowTableCell,
} from '../../../common/components';
import { GROUP_TEXTS } from '../constants/materialGroupsConstants';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import { MATERIAL_OPTION_TEXTS } from '../../constants/materialOptionTexts';
import { toArticleOptions } from '../../utils/toArticleOptions';

const DRAWER_WIDTH = { xs: '100vw', md: 'min(720px, calc(100vw - 40px))' };

/**
 * Drawer de alta y edición de un grupo de materiales (sin precios ni cantidades de receta).
 * @param {Object} props
 * @param {boolean} props.open Visibilidad del drawer.
 * @param {Object|null} props.form Formulario en edición.
 * @param {Array<Object>} props.articleOptions Resultado de la búsqueda de artículos.
 * @param {boolean} props.isSearchingArticles Búsqueda en curso.
 * @param {string} props.error Mensaje de error del guardado.
 * @param {boolean} props.isSaving Guardado en curso.
 * @param {boolean} props.canSave Habilita el guardado.
 * @param {Function} props.onClose Cierra el drawer.
 * @param {Function} props.onChange Cambia código o nombre.
 * @param {Function} props.onArticleSearch Actualiza el texto de búsqueda.
 * @param {Function} props.onAddArticle Agrega un integrante.
 * @param {Function} props.onRemoveArticle Quita un integrante.
 * @param {Function} props.onSave Guarda la revisión.
 * @returns {JSX.Element}
 */
const MaterialGroupDrawer = ({
  open,
  form,
  articleOptions,
  isSearchingArticles,
  error,
  isSaving,
  canSave,
  onClose,
  onChange,
  onArticleSearch,
  onAddArticle,
  onRemoveArticle,
  onSave,
}) => {
  const articles = form?.articles || [];

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={form?.uuid ? GROUP_TEXTS.editTitle : GROUP_TEXTS.createTitle}
      width={DRAWER_WIDTH}
      footer={(
        <DrawerFormActions
          summary={(
            <DrawerFormActionSummary
              label={GROUP_TEXTS.membersSummary}
              value={GROUP_TEXTS.membersCount(articles.length)}
            />
          )}
        >
          <CustomButton variant="outlined" onClick={onClose} disabled={isSaving}>
            {GROUP_TEXTS.cancel}
          </CustomButton>
          <CustomButton
            variant="base"
            startIcon={<Save />}
            onClick={onSave}
            isLoading={isSaving}
            disabled={!canSave}
          >
            {GROUP_TEXTS.save}
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
        {error && (
          <Box sx={{ mx: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <DrawerFormSection title={GROUP_TEXTS.dataSection}>
          <CustomTextField
            label={GROUP_TEXTS.code}
            name="code"
            value={form?.code || ''}
            onChange={onChange}
            required
            sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 4' } }}
          />
          <CustomTextField
            label={GROUP_TEXTS.name}
            name="name"
            value={form?.name || ''}
            onChange={onChange}
            required
            sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 8' } }}
          />
        </DrawerFormSection>

        <DrawerFormSection title={GROUP_TEXTS.membersSection} grid={false}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <CustomAutoComplete
              label={GROUP_TEXTS.articleSearch}
              value={null}
              loading={isSearchingArticles}
              options={toArticleOptions(articleOptions)}
              renderOption={renderTwoLineOption}
              inputPlaceholder={MATERIAL_OPTION_TEXTS.searchArticle}
              ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
              onInputChange={(_, value, reason) => {
                if (reason !== 'reset') onArticleSearch(value);
              }}
              filterOptions={(options) => options}
              onChange={(_, option) => onAddArticle(option)}
              sx={{ mt: 0 }}
            />
            <ClientPaginatedTable rows={articles} resetKey={form?.uuid}>
              {(pageRows) => (
                <GeneralTableBox hasMaxHeight={false}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <HeadTableCell sx={{ width: 140 }}>{GROUP_TEXTS.code}</HeadTableCell>
                        <HeadTableCell>{GROUP_TEXTS.material}</HeadTableCell>
                        <HeadTableCell sx={{ width: 90 }}>{GROUP_TEXTS.actions}</HeadTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {articles.length ? (
                        pageRows.map((article) => (
                          <TableRow key={article.uuid} hover>
                            <RowTableCell>{article.code}</RowTableCell>
                            <RowTableCell sx={{ textAlign: 'left' }}>{article.name}</RowTableCell>
                            <RowTableCell>
                              <Tooltip title={GROUP_TEXTS.removeMember}>
                                <span>
                                  <ActionIconButton
                                    variant="delete"
                                    onClick={() => onRemoveArticle(article.uuid)}
                                    disabled={isSaving}
                                  />
                                </span>
                              </Tooltip>
                            </RowTableCell>
                          </TableRow>
                        ))
                      ) : (
                        <NoDataSkeleton
                          colSpan={3}
                          message={GROUP_TEXTS.noMembers}
                          showSubtitle={false}
                        />
                      )}
                    </TableBody>
                  </Table>
                </GeneralTableBox>
              )}
            </ClientPaginatedTable>
          </Box>
        </DrawerFormSection>
      </Box>
    </DrawerForm>
  );
};

export default MaterialGroupDrawer;
